import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Observable} from 'rxjs';
import {first} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {TixxSelectService} from '~core/services';
import {TixxSelectTranslationsService} from '~shared/translations';
import {ProcessConfigurationService} from '~core/services';
import {ProcessConfiguration} from '~core/models';
import {NotificationType} from '~core/enum/notification.enum';
import {DefaultNotificationService} from '~core/services';
import {Lottery} from '~core/models';
import {EventAdministrationService} from "~features/tixx-select/event-administration.service";

/**
 * Component that lets you interact with the special requirement settings
 */
@UntilDestroy()
@Component({
  selector: 'app-optional-checkbox',
  templateUrl: './optional-checkbox.component.html'
})
export class OptionalCheckboxComponent implements OnInit, OnDestroy {
  public optionalCheckboxForm: UntypedFormGroup;
  public spinnerName: string = 'specialRequirementSubmit';
  public processConfiguration: ProcessConfiguration;
  public yesOrNoDropdown: Array<any> = [];
  public quillModules = {
    toolbar: [
      ['link']
    ]
  };

  @Input() public selectedLottery: Lottery;

  constructor(
    public tixxSelectService: TixxSelectService,
    public buttonsTranslations: ButtonsTranslations,
    public tixxSelectOverviewService: EventAdministrationService,
    private tixxSelectTranslationService: TixxSelectTranslationsService,
    private notificationService: DefaultNotificationService,
    private formBuilder: UntypedFormBuilder,
    private processConfigurationService: ProcessConfigurationService,
    public flTranslations: FormLabelTranslationService,
    public spinnerService: SpinnerService,
  ) {
    this.processConfigurationService.castCurrentProcessConfiguration.pipe(untilDestroyed(this))
      .subscribe((data: ProcessConfiguration) => this.processConfiguration = data);

    this.yesOrNoDropdown.push(
      {value: true, status: this.flTranslations.i18nActivated},
      {value: false, status: this.flTranslations.i18nDeactivated},
    );
  }

  ngOnInit(): void {
    const event = this.selectedLottery.event;
    this.optionalCheckboxForm = this.formBuilder.group({
      id: [this.selectedLottery.id],
      specialRequirement: this.processConfiguration.specialRequirement,
      event: this.formBuilder.group({
        specialRequirementText: [event.specialRequirementText],
        specialRequirementDescription: [event.specialRequirementDescription, [Validators.maxLength(255)]],
        tixxId: [event.tixxId],
        name: [event.name],
        competition: [event.competition],
        date: [event.date.date],
        venue: [event.venue],
        heroImage: [event.heroImage],
        homeTeam: [event.homeTeam],
        homeTeamLogoUrl: [event.homeTeamLogoUrl],
        guestTeam: [event.guestTeam],
        guestTeamLogoUrl: [event.guestTeamLogoUrl],
        guidelineHeadline: [event.guidelineHeadline],
        guidelineText: [event.guidelineText]
      })
    });
  }

  ngOnDestroy(): void {
    this.tixxSelectOverviewService.toggleEditSpecialRequirementModal(false);
  }

  /**
   * Set the status of the special requirement.
   */
  public setSpecialRequirementStatus(status): Observable<any> {
    return this.processConfigurationService.update({specialRequirement: status, id: this.processConfiguration.id});
  }

  /**
   * Submits the form to the lottery endpoint
   */
  public async submitForm(): Promise<void> {
    if (this.optionalCheckboxForm.valid) {
      this.spinnerService.show(this.spinnerName);
      const formValue = this.optionalCheckboxForm.value;
      const newStatus = formValue.specialRequirement;
      let processConfiguration;

      if (this.processConfiguration.specialRequirement !== newStatus) {
        processConfiguration = await this.setSpecialRequirementStatus(newStatus).toPromise();
      }
      delete formValue.specialRequirement;

      this.tixxSelectService.updateLottery(this.selectedLottery.id, formValue).pipe(first())
        .subscribe({
          next: (res: Lottery) => {
            if (processConfiguration) {
              this.processConfigurationService.setCurrentProcessConfiguration(processConfiguration);
            }
            this.tixxSelectService.setSelectedLottery(res);
            this.spinnerService.hide(this.spinnerName);
            this.tixxSelectOverviewService.toggleEditSpecialRequirementModal(false);
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.tixxSelectTranslationService.i18nSuccessUpdateLottery
            });
          },
          error: (error: Error) => {
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.tixxSelectTranslationService.i18nErrorUpdateLottery
            });
            this.spinnerService.hide(this.spinnerName);
          }
        });
    }
  }

}
